import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as ChartJS from 'chart.js';

import {
  homeGetNhiemVuKhacAction,
  homeClearNhiemVuKhac,
} from '@app/store/reducers/home';

import { rangeDateReport, messageToast } from '@app/utils/define';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    error?.message ||
    (typeof error === 'string' ? error : '') ||
    error?.title ||
    '';

  if (!msg && error?.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const RANGE_CUSTOM = 'Custom Range';
const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return { from: toYMD(pair[0]), to: toYMD(pair[1]) };
};

const normalizeFromTo = (from, to) => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (f && t && t < f) return { from: t, to: f };
  return { from: f, to: t };
};

const ChartCtor = ChartJS?.Chart || ChartJS?.default || ChartJS;
if (
  ChartCtor &&
  typeof ChartCtor.register === 'function' &&
  Array.isArray(ChartJS.registerables)
) {
  ChartCtor.register(...ChartJS.registerables);
}

const StatusDonutChart = ({ doing = 0, done = 0, loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const total = toNum(doing) + toNum(done);

  const chartData = useMemo(() => {
    return {
      labels: ['Đang thực hiện', 'Hoàn thành'],
      datasets: [
        {
          data: [toNum(doing), toNum(done)],
          backgroundColor: ['#2563eb', '#10b981'],
          borderColor: ['#2563eb', '#10b981'],
          borderWidth: 1,
        },
      ],
    };
  }, [doing, done]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {}
      chartRef.current = null;
    }

    if (loading) return;
    if (!canvasRef.current) return;
    if (!ChartCtor) return;
    if (!total) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isV3Plus = typeof ChartCtor.register === 'function';

    const optionsV3 = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '60%',
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (it) => {
              const value = toNum(it?.parsed);
              const pct = total ? Math.round((value / total) * 100) : 0;
              return `${it?.label || ''}: ${value} (${pct}%)`;
            },
          },
        },
      },
    };

    const optionsV2 = {
      responsive: true,
      maintainAspectRatio: false,
      cutoutPercentage: 60,
      legend: { position: 'bottom' },
      tooltips: {
        callbacks: {
          label: (tooltipItem, data) => {
            const idx = tooltipItem.index;
            const label = (data.labels && data.labels[idx]) || '';
            const value = toNum(data?.datasets?.[0]?.data?.[idx]);
            const pct = total ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${pct}%)`;
          },
        },
      },
    };

    chartRef.current = new ChartCtor(ctx, {
      type: 'doughnut',
      data: chartData,
      options: isV3Plus ? optionsV3 : optionsV2,
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
    };
  }, [chartData, loading, total]);

  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="card-title mb-0" style={{ fontWeight: 700 }}>
          Cơ cấu trạng thái
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <Spinner animation="border" size="sm" />
            <span>Đang tải...</span>
          </div>
        ) : !total ? (
          <div className="text-muted">Không có dữ liệu để hiển thị biểu đồ.</div>
        ) : (
          <div style={{ height: 320 }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>
    </div>
  );
};

const TeamStackedBarChart = ({ teamRows = [], loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const chartData = useMemo(() => {
    const rows = Array.isArray(teamRows) ? teamRows : [];
    return {
      labels: rows.map((r) => r.teamName),
      datasets: [
        {
          label: 'Đang thực hiện',
          data: rows.map((r) => toNum(r.dangThucHien)),
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          borderWidth: 1,
        },
        {
          label: 'Hoàn thành',
          data: rows.map((r) => toNum(r.daHoanThanh)),
          backgroundColor: '#10b981',
          borderColor: '#10b981',
          borderWidth: 1,
        },
      ],
    };
  }, [teamRows]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch {}
      chartRef.current = null;
    }

    if (loading) return;
    if (!canvasRef.current) return;
    if (!ChartCtor) return;
    if (!chartData?.labels?.length) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isV3Plus = typeof ChartCtor.register === 'function';

    const optionsV3 = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            footer: (tooltipItems) => {
              const total = (tooltipItems || []).reduce(
                (sum, it) => sum + toNum(it?.parsed?.y),
                0
              );
              return `Tổng: ${total}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { autoSkip: false } },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
      },
    };

    const optionsV2 = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: 'bottom' },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          footer: (tooltipItems) => {
            const total = (tooltipItems || []).reduce(
              (sum, it) => sum + toNum(it?.yLabel),
              0
            );
            return `Tổng: ${total}`;
          },
        },
      },
      scales: {
        xAxes: [{ stacked: true, gridLines: { display: false }, ticks: { autoSkip: false } }],
        yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
      },
    };

    chartRef.current = new ChartCtor(ctx, {
      type: 'bar',
      data: chartData,
      options: isV3Plus ? optionsV3 : optionsV2,
    });

    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch {}
        chartRef.current = null;
      }
    };
  }, [chartData, loading]);

  return (
    <div className="card h-100">
      <div className="card-header">
        <div className="card-title mb-0" style={{ fontWeight: 700 }}>
          Khối lượng theo nhóm (Đang thực hiện / Hoàn thành)
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <Spinner animation="border" size="sm" />
            <span>Đang tải...</span>
          </div>
        ) : !chartData?.labels?.length ? (
          <div className="text-muted">Không có dữ liệu để hiển thị biểu đồ.</div>
        ) : (
          <div style={{ height: 320 }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </div>
    </div>
  );
};

const TrangChuNhiemVuKhac = ({
  globalRangeLabel = '',
  globalAppliedFrom = '',
  globalAppliedTo = '',
  globalVersion = 0,
}) => {
  const dispatch = useDispatch();

  const {
    nhiemVuKhacSummary,
    loadingNhiemVuKhacSummary,
    errorNhiemVuKhacSummary,
  } = useSelector((s) => s.home || {});

  const [rangeLabel, setRangeLabel] = useState(globalRangeLabel || '');
  const [fromDate, setFromDate] = useState(globalAppliedFrom || '');
  const [toDate, setToDate] = useState(globalAppliedTo || '');
  const [appliedFrom, setAppliedFrom] = useState(globalAppliedFrom || '');
  const [appliedTo, setAppliedTo] = useState(globalAppliedTo || '');

  useEffect(() => {
    if (!globalVersion) return;
    setRangeLabel(globalRangeLabel || '');
    setFromDate(globalAppliedFrom || '');
    setToDate(globalAppliedTo || '');
    setAppliedFrom(globalAppliedFrom || '');
    setAppliedTo(globalAppliedTo || '');
  }, [globalVersion, globalRangeLabel, globalAppliedFrom, globalAppliedTo]);

  useEffect(() => {
    dispatch(homeGetNhiemVuKhacAction({ From: appliedFrom, To: appliedTo }));
  }, [dispatch, appliedFrom, appliedTo]);

  useEffect(() => {
    return () => {
      dispatch(homeClearNhiemVuKhac());
    };
  }, [dispatch]);

  const lastErrRef = useRef('');
  useEffect(() => {
    if (!errorNhiemVuKhacSummary) return;
    const msg =
      getErrMsg(errorNhiemVuKhacSummary) ||
      'Không thể tải dữ liệu Trang chủ - Nhiệm vụ khác.';
    if (msg && msg !== lastErrRef.current) {
      lastErrRef.current = msg;
      toast.error(msg);
    }
  }, [errorNhiemVuKhacSummary]);

  const summary = nhiemVuKhacSummary || {};

  const tongDangThucHien = toNum(
    summary.tongDangThucHien ?? summary.TongDangThucHien ?? 0
  );
  const tongDaHoanThanh = toNum(
    summary.tongDaHoanThanh ?? summary.TongDaHoanThanh ?? 0
  );

  const theoTeamsRaw = Array.isArray(summary.theoTeams ?? summary.TheoTeams)
    ? (summary.theoTeams ?? summary.TheoTeams)
    : [];

  const teamRows = useMemo(() => {
    return theoTeamsRaw
      .map((x) => ({
        teamId: toNum(x?.teamId ?? x?.TeamId ?? 0),
        teamName: String(x?.tenTeam ?? x?.TenTeam ?? '').trim(),
        dangThucHien: toNum(x?.dangThucHien ?? x?.DangThucHien ?? 0),
        daHoanThanh: toNum(x?.daHoanThanh ?? x?.DaHoanThanh ?? 0),
      }))
      .filter((x) => x.teamId > 0 && x.teamName);
  }, [theoTeamsRaw]);

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (!v) {
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      return;
    }

    if (v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      const norm = normalizeFromTo(from, to);
      setFromDate(norm.from);
      setToDate(norm.to);
      setAppliedFrom(norm.from);
      setAppliedTo(norm.to);
    }
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel !== RANGE_CUSTOM) return;

    if (!fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }

    const norm = normalizeFromTo(fromDate, toDate);
    setFromDate(norm.from);
    setToDate(norm.to);
    setAppliedFrom(norm.from);
    setAppliedTo(norm.to);
  };

  const handleClearFilter = () => {
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  return (
    <Row>
      <Col lg={12}>
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="mb-0" style={{ fontWeight: 800, fontSize: 23 }}>
                Nhiệm vụ khác
              </h3>
            </div>
          </Card.Header>

          <Card.Body>
            <div className="d-flex flex-column align-items-start mb-3">
              <div className="d-flex flex-wrap align-items-end gap-2">
                <div style={{ minWidth: 220 }}>
                  <label className="form-label mb-1">Khoảng thời gian</label>
                  <select
                    className="form-select form-select-sm"
                    value={rangeLabel}
                    onChange={handleChangeRange}
                    disabled={loadingNhiemVuKhacSummary}
                  >
                    <option value="">-- Chọn --</option>
                    {RANGE_OPTIONS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                {rangeLabel === RANGE_CUSTOM && (
                  <>
                    <div>
                      <label className="form-label mb-1">Từ ngày</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        disabled={loadingNhiemVuKhacSummary}
                      />
                    </div>

                    <div>
                      <label className="form-label mb-1">Đến ngày</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        disabled={loadingNhiemVuKhacSummary}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleApplyCustomRange}
                      disabled={loadingNhiemVuKhacSummary}
                    >
                      Lọc
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleClearFilter}
                  disabled={loadingNhiemVuKhacSummary}
                >
                  Xóa lọc
                </button>
              </div>

              {(appliedFrom || appliedTo) && (
                <div className="filter-hint" style={{ marginTop: 6 }}>
                  Đang lọc : {fmtDDMMYYYY(appliedFrom) || '...'} →{' '}
                  {fmtDDMMYYYY(appliedTo) || '...'}
                </div>
              )}
            </div>

            <Row>
              <Col lg={4} className="mb-3 mb-lg-0">
                <StatusDonutChart
                  doing={tongDangThucHien}
                  done={tongDaHoanThanh}
                  loading={loadingNhiemVuKhacSummary}
                />
              </Col>

              <Col lg={8}>
                <TeamStackedBarChart
                  teamRows={teamRows}
                  loading={loadingNhiemVuKhacSummary}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default TrangChuNhiemVuKhac;
